import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  getGetSupplierBillQueryKey,
  getGetSupplierBillReceiptUrl,
  getListPurchasesQueryKey,
  getListSupplierBillsQueryKey,
  useAttachSupplierBillReceipt,
  useCreateSupplierBill,
  useGetSupplierBill,
  useListPurchases,
  useListSupplierBills,
  useListSuppliers,
  useRecordSupplierBillPayment,
  useRequestSupplierBillReceiptUploadUrl,
} from "@workspace/api-client-react";
import type { SupplierBill, SupplierBillPaymentInput } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { formatCurrency } from "@/lib/currency";
import {
  AlertCircle, ArrowDownToLine, ArrowUpRight, Check, Clock3,
  FileText, Paperclip, Plus, Search, UploadCloud, Wallet, X,
} from "lucide-react";
import { format } from "date-fns";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
const ALLOWED_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/webp"];
const MAX_RECEIPT_BYTES = 10 * 1024 * 1024;
const MAX_SAFE_KOBO = BigInt(Number.MAX_SAFE_INTEGER);
const koboToNaira = (value: string | number) => Number(value || 0) / 100;
const inputToKobo = (value: string): string | null => {
  const match = /^(?:([0-9]+)(?:\.([0-9]{0,2}))?|\.([0-9]{1,2}))$/.exec(value.trim());
  if (!match) return null;
  const whole = match[1] ?? "0";
  const fraction = match[2] ?? match[3] ?? "";
  const kobo = BigInt(whole) * 100n + BigInt(fraction.padEnd(2, "0") || "0");
  return kobo > 0n && kobo <= MAX_SAFE_KOBO ? kobo.toString() : null;
};
const watInputValues = () => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Africa/Lagos",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date());
  const part = (type: string) => parts.find(item => item.type === type)?.value ?? "";
  const date = `${part("year")}-${part("month")}-${part("day")}`;
  return { date, dateTime: `${date}T${part("hour")}:${part("minute")}` };
};
const watDate = () => watInputValues().date;
const watDateTimeInput = () => watInputValues().dateTime;
const watDateTimeToIso = (value: string) => {
  const parsed = new Date(`${value}:00+01:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
};
const dateLabel = (value: string) => {
  if (!value) return "—";
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T12:00:00`) : new Date(value);
  return format(date, "dd MMM yyyy");
};

const STATUS_LABEL: Record<string, string> = {
  unpaid: "Unpaid", partially_paid: "Part paid", paid: "Paid",
  upcoming: "Upcoming", due_soon: "Due soon", overdue: "Overdue", settled: "Settled",
};
const statusClass: Record<string, string> = {
  unpaid: "border-[#ead8b7] bg-[#fbf1dd] text-[#8a5d18]",
  partially_paid: "border-[#c9ddd5] bg-[#eaf4ef] text-[#28644b]",
  paid: "border-[#c9ddd5] bg-[#eaf4ef] text-[#28644b]",
  upcoming: "border-[#d9e3dd] bg-[#edf4ef] text-[#456b59]",
  due_soon: "border-[#ead8b7] bg-[#fbf1dd] text-[#8a5d18]",
  overdue: "border-[#e6c7c1] bg-[#f8e9e6] text-[#a24a3e]",
  settled: "border-[#c9ddd5] bg-[#eaf4ef] text-[#28644b]",
};

export default function SupplierBills() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: billList, isLoading, isError, refetch } = useListSupplierBills();
  const { data: suppliers = [] } = useListSuppliers();
  const { data: purchases = [] } = useListPurchases();
  const createBill = useCreateSupplierBill();
  const requestUpload = useRequestSupplierBillReceiptUploadUrl();
  const attachReceipt = useAttachSupplierBillReceipt();
  const recordPayment = useRecordSupplierBillPayment();

  const [createOpen, setCreateOpen] = useState(false);
  const [detailBillId, setDetailBillId] = useState<number | null>(null);
  const [paymentBill, setPaymentBill] = useState<SupplierBill | null>(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [supplierId, setSupplierId] = useState("");
  const [purchaseId, setPurchaseId] = useState("");
  const [billNumber, setBillNumber] = useState("");
  const [billDate, setBillDate] = useState(watDate());
  const [dueDate, setDueDate] = useState("");
  const [totalAmount, setTotalAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [formError, setFormError] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<SupplierBillPaymentInput["method"]>("bank_transfer");
  const [paymentNote, setPaymentNote] = useState("");
  const [paymentDate, setPaymentDate] = useState(watDateTimeInput());
  const [paymentError, setPaymentError] = useState("");
  const [saving, setSaving] = useState(false);

  const { data: detail, isLoading: detailLoading, isError: detailError } = useGetSupplierBill(detailBillId ?? 0, {
    query: { enabled: !!detailBillId, queryKey: getGetSupplierBillQueryKey(detailBillId ?? 0) },
  });
  const filteredBills = useMemo(() => (billList?.bills ?? []).filter(bill => {
    const text = query.trim().toLowerCase();
    const matchesText = !text || bill.supplierName.toLowerCase().includes(text) || bill.billNumber.toLowerCase().includes(text) || (bill.purchaseOrderNumber ?? "").toLowerCase().includes(text);
    return matchesText && (statusFilter === "all" || bill.dueStatus === statusFilter || bill.paymentStatus === statusFilter);
  }), [billList?.bills, query, statusFilter]);

  const linkedPurchases = purchases.filter((purchase: any) =>
    String(purchase.supplierId) === supplierId && !purchase.supplierBillId && purchase.status !== "cancelled",
  );

  const invalidateBills = async (id?: number) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getListSupplierBillsQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getListPurchasesQueryKey() }),
      queryClient.invalidateQueries({ queryKey: ["supplier-ledger"] }),
      ...(id ? [queryClient.invalidateQueries({ queryKey: getGetSupplierBillQueryKey(id) })] : []),
    ]);
  };

  const clearCreateForm = () => {
    setSupplierId(""); setPurchaseId(""); setBillNumber(""); setBillDate(watDate());
    setDueDate(""); setTotalAmount(""); setNotes(""); setReceiptFile(null); setFormError("");
  };

  const validateReceipt = (file: File | null) => {
    if (!file) return "";
    if (!ALLOWED_TYPES.includes(file.type)) return "Choose a PDF, JPEG, PNG or WebP receipt.";
    if (file.size < 1 || file.size > MAX_RECEIPT_BYTES) return "Receipt must be 10 MiB or smaller.";
    if (file.name.length > 180) return "Receipt file name must be 180 characters or fewer.";
    return "";
  };

  const attachFile = async (id: number, file: File) => {
    const signed = await requestUpload.mutateAsync({ id, data: { name: file.name, size: file.size, contentType: file.type } });
    const upload = await fetch(signed.uploadURL, {
      method: "PUT",
      headers: { "Content-Type": file.type },
      body: file,
    });
    if (!upload.ok) throw new Error("The receipt file could not be uploaded.");
    await attachReceipt.mutateAsync({
      id,
      data: { objectPath: signed.objectPath, fileName: file.name, contentType: file.type as any, size: file.size },
    });
  };

  const submitBill = async () => {
    setFormError("");
    const fileError = validateReceipt(receiptFile);
    if (fileError) { setFormError(fileError); return; }
    const totalKobo = inputToKobo(totalAmount);
    if (!supplierId || !billNumber.trim() || !billDate || !dueDate || !totalKobo) {
      setFormError("Complete the supplier, invoice number, dates and invoice total.");
      return;
    }
    if (dueDate < billDate) { setFormError("Due date cannot be before the invoice date."); return; }
    const linkedPO = purchaseId ? purchases.find((p: any) => String(p.id) === purchaseId) as any : null;
    if (linkedPO?.supplierBillId) { setFormError("This purchase order is already managed by a supplier bill."); return; }
    setSaving(true);
    try {
      const saved = await createBill.mutateAsync({
        data: {
          supplierId: Number(supplierId),
          purchaseId: purchaseId ? Number(purchaseId) : undefined,
          billNumber: billNumber.trim(),
          billDate,
          dueDate,
          totalAmountKobo: totalKobo,
          notes: notes.trim() || undefined,
        },
      });
      await invalidateBills(saved.id);
      setDetailBillId(saved.id);
      setCreateOpen(false);
      clearCreateForm();
      if (receiptFile) {
        try {
          await attachFile(saved.id, receiptFile);
          await invalidateBills(saved.id);
          toast({ title: "Bill saved", description: "Invoice and receipt are attached." });
        } catch {
          await invalidateBills(saved.id);
          toast({ title: "Bill saved; receipt not attached", description: "The invoice is recorded. Open it to attach the receipt later.", variant: "destructive" });
        }
      } else {
        toast({ title: "Supplier bill saved", description: `${saved.billNumber} is now in your bill register.` });
      }
    } catch (error) {
      setFormError(error instanceof Error ? error.message : "The bill could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  const submitPayment = async () => {
    setPaymentError("");
    if (!paymentBill) return;
    const naira = Number(paymentAmount);
    if (!Number.isFinite(naira) || naira <= 0) { setPaymentError("Enter a payment greater than zero."); return; }
    const amountKobo = inputToKobo(paymentAmount);
    if (!amountKobo) { setPaymentError("Enter an amount with no more than two decimal places."); return; }
    if (BigInt(amountKobo) > BigInt(paymentBill.balanceKobo)) { setPaymentError("Payment cannot exceed the remaining bill balance."); return; }
    const paidAt = watDateTimeToIso(paymentDate);
    if (!paidAt) { setPaymentError("Enter a valid payment date and time."); return; }
    try {
      await recordPayment.mutateAsync({
        id: paymentBill.id,
        data: {
          amountKobo,
          paidAt,
          method: paymentMethod,
          note: paymentNote.trim() || undefined,
        },
      });
      await invalidateBills(paymentBill.id);
      setPaymentBill(null); setPaymentAmount(""); setPaymentNote("");
      toast({ title: "Payment recorded", description: `${formatCurrency(naira)} applied to ${paymentBill.billNumber}.` });
    } catch (error) {
      setPaymentError(error instanceof Error ? error.message : "Payment could not be recorded.");
    }
  };

  const addReceiptToSavedBill = async () => {
    if (!detail || !receiptFile) return;
    const error = validateReceipt(receiptFile);
    if (error) {
      toast({ title: "Receipt cannot be attached", description: error, variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      await attachFile(detail.id, receiptFile);
      await invalidateBills(detail.id);
      setReceiptFile(null);
      toast({ title: "Receipt attached", description: `${receiptFile.name} is linked to ${detail.billNumber}.` });
    } catch {
      toast({ title: "Receipt could not be attached", description: "The bill remains saved. You can try attaching the file again.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const openReceipt = (id: number) => {
    const opened = window.open(`${BASE}${getGetSupplierBillReceiptUrl(id)}`, "_blank", "noopener,noreferrer");
    if (!opened) {
      toast({ title: "Receipt blocked", description: "Allow pop-ups for this app to open the receipt.", variant: "destructive" });
    }
  };

  const summary = billList?.summary;

  return (
    <main className="supplier-bills-page mx-auto max-w-[1440px] space-y-7 px-4 py-6 sm:px-7 lg:px-9">
      <header className="ledger-enter flex flex-col justify-between gap-5 border-b border-[#e7e1d5] pb-6 sm:flex-row sm:items-end">
        <div>
          <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[.17em] text-[#557468]">
            <span className="h-2 w-2 rounded-full bg-[#54866d]" /> Payables / Supplier bills
          </div>
          <h1 className="text-3xl font-extrabold tracking-[-.04em] text-[#20343b] sm:text-[38px]">Supplier bills</h1>
          <p className="mt-1 max-w-xl text-sm text-[#697a7a]">The invoice is the payable record. Purchase orders stay separate until a bill takes over.</p>
        </div>
        <Button data-testid="button-create-supplier-bill" onClick={() => setCreateOpen(true)} className="h-11 gap-2 rounded-lg bg-[#2d7055] px-5 shadow-sm transition-transform hover:-translate-y-0.5 hover:bg-[#255e47]">
          <Plus className="h-4 w-4" /> Record supplier bill
        </Button>
      </header>

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Metric label="Open bills" value={summary?.openBillCount ?? 0} caption="Invoices with a remaining balance" icon={<FileText className="h-4 w-4" />} />
        <Metric label="Outstanding balance" value={summary ? formatCurrency(koboToNaira(summary.openBalanceKobo)) : "—"} caption="Across all open invoices" icon={<Wallet className="h-4 w-4" />} emphasis />
        <Metric label="Due soon" value={summary ? formatCurrency(koboToNaira(summary.dueSoonBalanceKobo)) : "—"} caption="Coming due in the next 7 days" icon={<Clock3 className="h-4 w-4" />} />
        <Metric label="Overdue" value={summary ? formatCurrency(koboToNaira(summary.overdueBalanceKobo)) : "—"} caption={`${summary?.overdueBillCount ?? 0} invoice${summary?.overdueBillCount === 1 ? "" : "s"} need attention`} icon={<AlertCircle className="h-4 w-4" />} warning />
      </section>

      <section className="overflow-hidden rounded-xl border border-[#e7e1d5] bg-[#fbf8f0] shadow-[0_8px_28px_rgba(37,53,45,.045)]">
        <div className="flex flex-col gap-4 border-b border-[#e7e1d5] p-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div>
            <h2 className="font-bold text-[#20343b]">Invoice register</h2>
            <p className="mt-0.5 text-xs text-[#798783]">Supplier-stated totals, due dates and payments</p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative sm:w-64">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#82908a]" />
              <Input data-testid="input-search-supplier-bills" value={query} onChange={e => setQuery(e.target.value)} placeholder="Bill, order or supplier" className="h-9 border-[#e2ddcf] bg-[#fffdf8] pl-9" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="select-bill-status" className="h-9 w-full border-[#e2ddcf] bg-[#fffdf8] sm:w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All bills</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="due_soon">Due soon</SelectItem>
                <SelectItem value="upcoming">Upcoming</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {isError ? (
          <div className="flex flex-col items-center gap-3 px-5 py-16 text-center">
            <AlertCircle className="h-8 w-8 text-[#a24a3e]" />
            <div><p className="font-semibold text-[#20343b]">Bills did not load</p><p className="mt-1 text-sm text-[#697a7a]">Your saved records are unchanged. Try again.</p></div>
            <Button variant="outline" onClick={() => void refetch()}>Retry</Button>
          </div>
        ) : isLoading ? <BillSkeleton /> : filteredBills.length === 0 ? (
          <div className="flex flex-col items-center px-5 py-16 text-center">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#e9eee7] text-[#45745b]"><FileText className="h-5 w-5" /></div>
            <p className="font-bold text-[#20343b]">{billList?.bills.length ? "No matching bills" : "No supplier invoices yet"}</p>
            <p className="mt-1 max-w-sm text-sm text-[#697a7a]">{billList?.bills.length ? "Adjust your search or status filter." : "Record an invoice when the supplier's bill arrives. Its total and payable balance stay distinct from the purchase order."}</p>
            {!billList?.bills.length && <Button onClick={() => setCreateOpen(true)} variant="outline" className="mt-4 gap-2 border-[#cdd8d0]"><Plus className="h-4 w-4" /> Record first bill</Button>}
          </div>
        ) : (
          <>
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full min-w-[850px] text-sm">
                <thead><tr className="border-b border-[#e7e1d5] bg-[#f6f2e9] text-left text-[10px] font-bold uppercase tracking-[.12em] text-[#7d8982]">
                  <th className="px-5 py-3">Invoice / supplier</th><th className="px-4 py-3">Invoice date</th><th className="px-4 py-3">Due date</th><th className="px-4 py-3 text-right">Invoice total</th><th className="px-4 py-3 text-right">Balance</th><th className="px-4 py-3">Status</th><th className="px-5 py-3 text-right"> </th>
                </tr></thead>
                <tbody>{filteredBills.map(bill => <tr key={bill.id} data-testid={`row-supplier-bill-${bill.id}`} className="border-b border-[#eee9df] transition-colors hover:bg-[#f6f3eb] last:border-0">
                  <td className="px-5 py-4">
                    <button className="text-left" onClick={() => setDetailBillId(bill.id)} data-testid={`button-open-bill-${bill.id}`}>
                      <span className="ledger-mono block font-semibold text-[#20343b]">{bill.billNumber}</span><span className="mt-1 block text-xs text-[#697a7a]">{bill.supplierName}</span>
                    </button>
                    {bill.purchaseOrderNumber && <span className="mt-1 inline-flex items-center gap-1 text-[10px] text-[#687b70]"><ArrowUpRight className="h-3 w-3" /> Linked to {bill.purchaseOrderNumber}</span>}
                  </td>
                  <td className="px-4 py-4 text-[#566968]">{dateLabel(bill.billDate)}</td>
                  <td className="px-4 py-4 text-[#566968]">{dateLabel(bill.dueDate)}</td>
                  <td className="ledger-mono px-4 py-4 text-right font-medium text-[#334842]">{formatCurrency(koboToNaira(bill.totalAmountKobo))}</td>
                  <td className={`ledger-mono px-4 py-4 text-right font-bold ${Number(bill.balanceKobo) ? "text-[#9b6230]" : "text-[#47765e]"}`}>{formatCurrency(koboToNaira(bill.balanceKobo))}</td>
                  <td className="px-4 py-4"><StatusBadge status={bill.dueStatus === "settled" ? bill.paymentStatus : bill.dueStatus} /></td>
                  <td className="px-5 py-4 text-right"><Button variant="ghost" size="sm" onClick={() => setDetailBillId(bill.id)} className="text-[#44735a] hover:bg-[#e8efe9]">View</Button></td>
                </tr>)}</tbody>
              </table>
            </div>
            <div className="divide-y divide-[#eee9df] md:hidden">{filteredBills.map(bill => <article key={bill.id} className="space-y-3 p-4" data-testid={`card-supplier-bill-${bill.id}`}>
              <div className="flex items-start justify-between gap-3"><div><button onClick={() => setDetailBillId(bill.id)} className="ledger-mono text-left font-bold text-[#20343b]">{bill.billNumber}</button><p className="mt-1 text-xs text-[#697a7a]">{bill.supplierName}</p></div><StatusBadge status={bill.dueStatus === "settled" ? bill.paymentStatus : bill.dueStatus} /></div>
              <div className="grid grid-cols-2 gap-2 text-xs"><p className="text-[#78847e]">Invoice total <span className="ledger-mono mt-1 block text-sm font-semibold text-[#334842]">{formatCurrency(koboToNaira(bill.totalAmountKobo))}</span></p><p className="text-[#78847e]">Balance <span className="ledger-mono mt-1 block text-sm font-semibold text-[#9b6230]">{formatCurrency(koboToNaira(bill.balanceKobo))}</span></p><p className="text-[#78847e]">Invoice date <span className="mt-1 block text-[#425754]">{dateLabel(bill.billDate)}</span></p><p className="text-[#78847e]">Due date <span className="mt-1 block text-[#425754]">{dateLabel(bill.dueDate)}</span></p></div>
              {bill.purchaseOrderNumber && <p className="text-xs text-[#687b70]">Linked PO · {bill.purchaseOrderNumber}</p>}
              <Button variant="outline" size="sm" onClick={() => setDetailBillId(bill.id)} className="w-full border-[#d9e2da] text-[#44735a]">Open invoice</Button>
            </article>)}</div>
          </>
        )}
      </section>

      <Dialog open={createOpen} onOpenChange={open => { setCreateOpen(open); if (!open) clearCreateForm(); }}>
        <DialogContent className="max-h-[92dvh] max-w-xl overflow-y-auto border-[#e7e1d5] bg-[#fbf8f0]">
          <DialogHeader><DialogTitle className="font-display text-xl text-[#20343b]">Record supplier invoice</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="rounded-lg border border-[#d9e3dc] bg-[#eef4ef] px-3 py-2 text-xs leading-5 text-[#557064]">Enter the amount printed on the supplier invoice. Linking a PO carries prior payments forward; this bill becomes the only payable record for that order.</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Supplier"><Select value={supplierId} onValueChange={v => { setSupplierId(v); setPurchaseId(""); }}><SelectTrigger data-testid="select-bill-supplier"><SelectValue placeholder="Choose supplier" /></SelectTrigger><SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>)}</SelectContent></Select></Field>
              <Field label="Invoice number"><Input data-testid="input-bill-number" value={billNumber} onChange={e => setBillNumber(e.target.value)} placeholder="e.g. INV-2048" maxLength={100} /></Field>
              <Field label="Invoice date"><Input data-testid="input-bill-date" type="date" value={billDate} onChange={e => setBillDate(e.target.value)} /></Field>
              <Field label="Due date"><Input data-testid="input-bill-due-date" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} /></Field>
              <Field label="Invoice total (₦)"><Input data-testid="input-bill-total" type="number" min="0.01" step="0.01" value={totalAmount} onChange={e => setTotalAmount(e.target.value)} placeholder="0.00" /></Field>
              <Field label="Link purchase order"><Select value={purchaseId || "none"} onValueChange={v => setPurchaseId(v === "none" ? "" : v)} disabled={!supplierId}><SelectTrigger data-testid="select-bill-purchase"><SelectValue placeholder={supplierId ? "Optional PO" : "Choose supplier first"} /></SelectTrigger><SelectContent><SelectItem value="none">No linked PO</SelectItem>{linkedPurchases.map((p: any) => <SelectItem key={p.id} value={String(p.id)}>{p.orderNumber}</SelectItem>)}</SelectContent></Select></Field>
            </div>
            <Field label="Notes"><Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional reference or context" maxLength={2000} /></Field>
            <ReceiptPicker file={receiptFile} onChange={setReceiptFile} />
            {formError && <p role="alert" className="flex items-center gap-2 text-sm text-[#a24a3e]"><AlertCircle className="h-4 w-4 shrink-0" />{formError}</p>}
          </div>
          <DialogFooter className="mt-1">
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button data-testid="button-save-supplier-bill" onClick={() => void submitBill()} disabled={saving || createBill.isPending} className="gap-2 bg-[#2d7055] hover:bg-[#255e47]">
              {saving ? <span className="h-4 w-4 animate-pulse rounded-full bg-white/70" /> : <Check className="h-4 w-4" />} Save bill
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detailBillId} onOpenChange={open => { if (!open) { setDetailBillId(null); setReceiptFile(null); } }}>
        <DialogContent className="max-h-[92dvh] max-w-2xl overflow-y-auto border-[#e7e1d5] bg-[#fbf8f0]">
          {detailLoading ? <BillDetailSkeleton /> : detailError ? <div className="space-y-3 py-8 text-center"><AlertCircle className="mx-auto h-8 w-8 text-[#a24a3e]" /><p className="font-semibold">Invoice details unavailable</p><Button variant="outline" onClick={() => setDetailBillId(null)}>Close</Button></div> : detail && <>
            <DialogHeader><DialogTitle className="flex flex-wrap items-center gap-3 font-display text-xl text-[#20343b]">{detail.billNumber}<StatusBadge status={detail.dueStatus === "settled" ? detail.paymentStatus : detail.dueStatus} /></DialogTitle></DialogHeader>
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-3 rounded-xl border border-[#e7e1d5] bg-[#f6f2e9] p-4 sm:grid-cols-4">
                <DetailStat label="Supplier" value={detail.supplierName} />
                <DetailStat label="Invoice date" value={dateLabel(detail.billDate)} />
                <DetailStat label="Due date" value={dateLabel(detail.dueDate)} />
                <DetailStat label="Purchase order" value={detail.purchaseOrderNumber || "Not linked"} />
                <DetailStat label="Invoice total" value={formatCurrency(koboToNaira(detail.totalAmountKobo))} strong />
                <DetailStat label="Carried from PO" value={formatCurrency(koboToNaira(detail.openingPaidKobo))} />
                <DetailStat label="New bill payments" value={formatCurrency(Math.max(0, koboToNaira(detail.amountPaidKobo) - koboToNaira(detail.openingPaidKobo)))} />
                <DetailStat label="Remaining balance" value={formatCurrency(koboToNaira(detail.balanceKobo))} strong />
              </div>
              {detail.openingPaidKobo !== "0" && <p className="rounded-lg border border-[#d9e3dc] bg-[#eef4ef] px-3 py-2 text-xs text-[#557064]">Carried-forward PO payments are shown separately from payments recorded against this invoice.</p>}
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="font-bold text-[#20343b]">Receipt</h3>
                {detail.receiptFileName ? <div className="flex flex-wrap items-center gap-2">
                  <span className="flex items-center gap-2 rounded-lg border border-[#e7e1d5] bg-[#fffdf8] px-3 py-2 text-xs text-[#536760]"><Paperclip className="h-4 w-4 text-[#54866d]" />{detail.receiptFileName}</span>
                  <Button variant="outline" size="sm" onClick={() => openReceipt(detail.id)} className="gap-1.5"><ArrowDownToLine className="h-3.5 w-3.5" /> Open receipt</Button>
                </div> : <span className="text-xs text-[#7b8880]">No receipt attached</span>}
              </div>
              {!detail.receiptFileName && <div className="space-y-2"><ReceiptPicker file={receiptFile} onChange={setReceiptFile} compact /><Button onClick={() => void addReceiptToSavedBill()} disabled={!receiptFile || saving} variant="outline" size="sm" className="gap-2 border-[#cdd8d0] text-[#35684f]"><UploadCloud className="h-4 w-4" /> Attach receipt</Button></div>}
              <div className="flex items-center justify-between border-t border-[#e7e1d5] pt-4">
                <h3 className="font-bold text-[#20343b]">Payments on this bill</h3>
                {BigInt(detail.balanceKobo) > 0n && <Button onClick={() => { setPaymentBill(detail); setPaymentAmount(""); setPaymentError(""); setPaymentDate(watDateTimeInput()); }} className="gap-2 bg-[#2d7055] hover:bg-[#255e47]"><Wallet className="h-4 w-4" /> Record payment</Button>}
              </div>
              <div className="divide-y divide-[#ece6da] rounded-lg border border-[#e7e1d5] bg-[#fffdf8]">
                {detail.openingPaidKobo !== "0" && <div className="flex items-center justify-between gap-3 px-3 py-3 text-sm"><div><p className="font-medium text-[#405950]">Carried forward from {detail.purchaseOrderNumber || "purchase order"}</p><p className="mt-0.5 text-xs text-[#87918a]">Paid before invoice was linked</p></div><span className="ledger-mono font-semibold text-[#47765e]">{formatCurrency(koboToNaira(detail.openingPaidKobo))}</span></div>}
                {detail.payments.map(payment => <div key={payment.id} className="flex items-center justify-between gap-3 px-3 py-3 text-sm"><div><p className="font-medium text-[#405950]">{payment.method.replace("_", " ")}</p><p className="mt-0.5 text-xs text-[#87918a]">{format(new Date(payment.paidAt), "dd MMM yyyy, HH:mm")}{payment.note ? ` · ${payment.note}` : ""}</p></div><span className="ledger-mono font-semibold text-[#47765e]">{formatCurrency(koboToNaira(payment.amountKobo))}</span></div>)}
                {!detail.openingPaidKobo || detail.openingPaidKobo === "0" ? detail.payments.length === 0 && <p className="px-4 py-6 text-center text-sm text-[#798783]">No payments recorded against this invoice.</p> : null}
              </div>
              {detail.notes && <p className="text-sm text-[#687771]"><span className="font-semibold text-[#405950]">Notes</span> · {detail.notes}</p>}
            </div>
          </>}
        </DialogContent>
      </Dialog>

      <Dialog open={!!paymentBill} onOpenChange={open => { if (!open) setPaymentBill(null); }}>
        <DialogContent className="max-w-md border-[#e7e1d5] bg-[#fbf8f0]">
          <DialogHeader><DialogTitle className="font-display text-xl text-[#20343b]">Record bill payment</DialogTitle></DialogHeader>
          {paymentBill && <div className="space-y-4">
            <div className="rounded-lg border border-[#e7e1d5] bg-[#f6f2e9] p-3 text-sm"><div className="flex justify-between"><span className="text-[#76827c]">Invoice</span><span className="ledger-mono font-medium">{paymentBill.billNumber}</span></div><div className="mt-2 flex justify-between"><span className="text-[#76827c]">Remaining balance</span><span className="ledger-mono font-bold text-[#9b6230]">{formatCurrency(koboToNaira(paymentBill.balanceKobo))}</span></div></div>
            <Field label="Amount (₦)"><Input data-testid="input-bill-payment-amount" type="number" min="0.01" max={koboToNaira(paymentBill.balanceKobo)} step="0.01" value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)} placeholder="0.00" /></Field>
          <div className="grid grid-cols-2 gap-3"><Field label="Payment date (WAT)"><Input type="datetime-local" value={paymentDate} onChange={e => setPaymentDate(e.target.value)} /></Field><Field label="Method"><Select value={paymentMethod} onValueChange={v => setPaymentMethod(v as SupplierBillPaymentInput["method"])}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="bank_transfer">Bank transfer</SelectItem><SelectItem value="cash">Cash</SelectItem><SelectItem value="card">Card</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent></Select></Field></div>
            <Field label="Note (optional)"><Input value={paymentNote} onChange={e => setPaymentNote(e.target.value)} placeholder="Transfer reference or note" maxLength={500} /></Field>
            {paymentError && <p role="alert" className="text-sm text-[#a24a3e]">{paymentError}</p>}
          </div>}
          <DialogFooter><Button variant="outline" onClick={() => setPaymentBill(null)}>Cancel</Button><Button onClick={() => void submitPayment()} disabled={recordPayment.isPending} className="bg-[#2d7055] hover:bg-[#255e47]">Record payment</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}

function Metric({ label, value, caption, icon, emphasis, warning }: { label: string; value: string | number; caption: string; icon: React.ReactNode; emphasis?: boolean; warning?: boolean }) {
  return <Card className={`ledger-enter rounded-xl border-[#e7e1d5] bg-[#fbf8f0] shadow-[0_5px_18px_rgba(37,53,45,.035)] ${warning ? "border-l-[3px] border-l-[#bd7057]" : emphasis ? "border-l-[3px] border-l-[#54866d]" : ""}`}><CardContent className="flex items-start justify-between p-4 sm:p-5"><div><p className="text-[11px] font-bold uppercase tracking-[.12em] text-[#798783]">{label}</p><p className={`ledger-mono mt-2 text-2xl font-semibold tracking-[-.04em] ${warning ? "text-[#a24a3e]" : "text-[#20343b]"}`}>{value}</p><p className="mt-1 text-xs text-[#7e8982]">{caption}</p></div><div className={`rounded-lg p-2 ${warning ? "bg-[#f8e9e6] text-[#a24a3e]" : "bg-[#e9eee7] text-[#45745b]"}`}>{icon}</div></CardContent></Card>;
}
function StatusBadge({ status }: { status: string }) { return <Badge variant="outline" className={`whitespace-nowrap border px-2 py-0.5 text-[10px] font-semibold ${statusClass[status] ?? ""}`}>{STATUS_LABEL[status] ?? status}</Badge>; }
function Field({ label, children }: { label: string; children: React.ReactNode }) { return <label className="block space-y-1.5 text-xs font-semibold text-[#576b64]">{label}{children}</label>; }
function DetailStat({ label, value, strong }: { label: string; value: string; strong?: boolean }) { return <div className="min-w-0"><p className="text-[10px] font-semibold uppercase tracking-[.08em] text-[#849087]">{label}</p><p className={`mt-1 truncate text-sm ${strong ? "ledger-mono font-bold text-[#20343b]" : "text-[#52665e]"}`}>{value}</p></div>; }
function ReceiptPicker({ file, onChange, compact }: { file: File | null; onChange: (file: File | null) => void; compact?: boolean }) {
  return <div className={`rounded-lg border border-dashed border-[#cdd8d0] bg-[#f6f6ee] ${compact ? "p-3" : "p-4"}`}>
    <div className="flex items-center gap-3"><div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#e8efe9] text-[#4e795f]"><UploadCloud className="h-4 w-4" /></div><div className="min-w-0 flex-1"><p className="text-xs font-semibold text-[#3e574d]">Attach receipt <span className="font-normal text-[#849087]">· optional</span></p><p className="text-[10px] text-[#849087]">PDF, JPEG, PNG or WebP · max 10 MiB</p></div><label className="cursor-pointer rounded-md border border-[#d9e2da] bg-[#fffdf8] px-3 py-1.5 text-xs font-semibold text-[#486b57] transition-colors hover:bg-[#edf4ef]">{file ? "Replace" : "Choose file"}<input data-testid="input-supplier-bill-receipt" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp" className="sr-only" onChange={e => onChange(e.target.files?.[0] ?? null)} /></label></div>
    {file && <div className="mt-3 flex items-center justify-between rounded-md bg-[#fffdf8] px-3 py-2 text-xs text-[#52665e]"><span className="flex min-w-0 items-center gap-2"><FileText className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{file.name}</span><span className="shrink-0 text-[#89948d]">{(file.size / 1024 / 1024).toFixed(2)} MiB</span></span><button type="button" aria-label="Remove receipt" onClick={() => onChange(null)} className="ml-2 rounded p-1 hover:bg-[#f5e8e4]"><X className="h-3.5 w-3.5" /></button></div>}
  </div>;
}
function BillSkeleton() { return <div className="space-y-3 p-5">{[0,1,2,3].map(i => <div key={i} className="h-14 animate-pulse rounded-lg bg-[#eee9df]" />)}</div>; }
function BillDetailSkeleton() { return <div className="space-y-3 py-4"><div className="h-7 w-48 animate-pulse rounded bg-[#eee9df]" /><div className="grid grid-cols-2 gap-3">{[0,1,2,3].map(i => <div key={i} className="h-16 animate-pulse rounded bg-[#eee9df]" />)}</div></div>; }
